import { Box, Button } from "@mui/material";
import { User } from "datasheets/model/core/User";
import { userAtom } from "datasheets/model/atoms/UserAtoms";
import { atom, useAtom, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import DataGrid from "react-data-grid";
import CreateUserDialog from "./CreateUserDialog";
import { useEffect, useState } from "react";
import { Cancel } from "@mui/icons-material";

const allUsersAtomValue = atom<User[] | Promise<User[]>>([]);

const allUsersAtomBase = atom(
  (get) => {
    return get(allUsersAtomValue);
  },
  async (get, set, _) =>
    set(
      allUsersAtomValue,
      fetch("/users").then((res) => res.json())
    )
);
allUsersAtomBase.onMount = (setAtom) => setAtom;

const allUsersAtom = loadable(allUsersAtomBase);

const updateAdminPermissionAtom = atom(null, (get, set, { user, isAdmin }) => {
  fetch("/user/admin", {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: isAdmin ? "PUT" : "DELETE",
    body: JSON.stringify({ user_id: user.id }),
  }).then(() => set(allUsersAtomBase, ""));
});

interface UserRow extends User {}

function rowKeyGetter(user: UserRow) {
  return user.id;
}
interface AdminFormatterProps {
  row: UserRow;
}
function AdminFormatter({ row }: AdminFormatterProps) {
  const updateAdminPermission = useSetAtom(updateAdminPermissionAtom);
  const value = row.isAdmin;
  function toggle() {
    updateAdminPermission({ user: row, isAdmin: !value });
  }
  const buttonText = !!value ? "Remove Admin" : "Make Admin";
  return <Button onClick={toggle}>{buttonText}</Button>;
}

function DeleteFormatter({ row }: AdminFormatterProps) {
  const refreshUserList = useSetAtom(allUsersAtomBase);
  const deleteUser = () => {
    fetch("/user", {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "DELETE",
      body: JSON.stringify({ user_id: row.id }),
    }).then(() => refreshUserList());
  };
  return <Cancel onClick={deleteUser} />;
}

const columns = ["id", "username", "name", "isAdmin", "delete"].map((name) => {
  let columnDef = { key: name, name: name };
  if (name === "isAdmin") {
    columnDef = { ...columnDef, ...{ formatter: AdminFormatter } };
  } else if (name === "delete") {
    columnDef = { ...columnDef, ...{ formatter: DeleteFormatter } };
  }
  return columnDef;
});

function getRows(users: User[]): UserRow[] {
  return users.map((user) => {
    return {
      id: user.id,
      username: user.username,
      name: user.name,
      isAdmin: user.isAdmin,
      fullname: () => user.name,
    };
  });
}

export default function ManageUsers() {
  const [users] = useAtom(allUsersAtom);
  const [user] = useAtom(userAtom);
  const rows = users.state === "hasData" ? getRows(users.data) : [];
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const refreshUserList = useSetAtom(allUsersAtomBase);
  function createUser() {
    setCreateUserModalOpen(true);
  }
  useEffect(() => {
    refreshUserList();
  }, [user, refreshUserList]); // Update if user changes
  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Button
          onClick={(e) => {
            e.preventDefault();
            window.location.href = "/";
          }}
        >
          Back to Main
        </Button>
        <Button onClick={createUser}>Create User</Button>
      </Box>
      <DataGrid
        columns={columns}
        rows={rows}
        rowKeyGetter={rowKeyGetter}
        className="ManageUsers"
      />
      <CreateUserDialog
        modalIsOpen={createUserModalOpen}
        setModalIsOpen={setCreateUserModalOpen}
        onComplete={refreshUserList}
      />
    </>
  );
}
